import { css } from '@linaria/core'
import { type FC, useMemo } from 'react'
import { ChangesTable } from '#pages/Configuration/Changes/ChangesTable'
import { type ComparisonResult, useChangesTable } from '#pages/Configuration/Changes/useChangesTable'
import { ComparisonSidebar } from '#pages/Configuration/Comparison/ComparisonSidebar'
import type { ConfigurationDiff } from '#pages/Configuration/Comparison/useConfigurationDifference'
import { OptionOverlay } from '#pages/Configuration/components/OptionOverlay'
import type { TruckConfiguration } from '#store/slices/configurationSlice'
import type { ConfigurationEnrichmentData } from '#store/slices/enrichConfiguration'
import { estimateChangeTableSize } from '../SummaryStep/utils'

const comparisonClass = css`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: row;
  overflow-y: hidden;

  > .sidebar {
    display: flex;
    flex-direction: column;
    width: 250px;

    > ._mode-selector {
      padding: 16px 24px 8px 24px;
    }

    > .comparison-sidebar {
      flex: 1;
      overflow-y: scroll;
    }
  }
`

interface OverrideChangeTableProps {
  baseConfiguration: TruckConfiguration | undefined
  baseEnrichment: ConfigurationEnrichmentData | undefined
  comparisonConfiguration: TruckConfiguration | undefined
  comparisonEnrichment: ConfigurationEnrichmentData | undefined
  difference: ConfigurationDiff | undefined
}

export const OverrideChangeTable: FC<OverrideChangeTableProps> = ({
  baseConfiguration,
  comparisonConfiguration,
  comparisonEnrichment,
  baseEnrichment,
  difference,
}: OverrideChangeTableProps) => {
  const comparisonResult = useMemo((): ComparisonResult => {
    if (!baseConfiguration || !comparisonConfiguration || !difference || !baseEnrichment || !comparisonEnrichment) {
      return {
        base: undefined,
        comparison: undefined,
        diff: {
          added: {},
          removed: {},
          selected: {},
          deselected: {},
          unchanged: {},
          priceChanged: {},
          byPath: {},
          affectedGroups: new Set(),
          affectedFamilies: new Set(),
          missingGroups: new Set(),
          missingFamilies: new Set(),
          priceChangeGroups: new Set(),
          priceChangeFamilies: new Set(),
        } satisfies ConfigurationDiff,
      }
    }

    // flip left and right
    return {
      base: {
        configuration: comparisonConfiguration,
        enrichment: comparisonEnrichment,
      },
      comparison: {
        configuration: baseConfiguration,
        enrichment: baseEnrichment,
      },
      diff: difference,
    }
  }, [baseConfiguration, comparisonConfiguration, baseEnrichment, comparisonEnrichment, difference])

  const context = useChangesTable(comparisonResult)
  const {
    displayedEntries,
    selectedEntries,
    diff,
    filter: { filterState, setFilterState },
    displayMode,
    setDisplayMode,
    virtualizer: { scrollTo },
  } = context

  return (
    <div className={comparisonClass}>
      <div className="sidebar">
        <ComparisonSidebar
          className="comparison-sidebar"
          filterState={filterState}
          setFilterState={setFilterState}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          displayedEntries={displayedEntries}
          diff={diff}
          selectedEntries={selectedEntries}
          scrollTo={scrollTo}
        />
      </div>
      <OptionOverlay />
      <ChangesTable context={context} headerHeight={65} estimateSize={estimateChangeTableSize} />
    </div>
  )
}
